import React, { useContext, useEffect, useRef } from 'react';
import { UserContext } from '../../context/UserProvider';

export default function Preferences({ onClose }) {
  const {
    darkMode,
    setDarkMode,
    isExpanded,
    setUserPreference,
    hideInActive,
    setHideInActive,
    hideDenied,
    setHideDenied,
    colorblindMode,
    setColorblindMode,
    localMode,
    setLocalMode,
    localFont,
    setLocalFont,
    localFontSize,
    setLocalFontSize,
    accessibilityHidden,
    setAccessibilityHidden,
  } = useContext(UserContext);

  const originalColorblind = useRef(colorblindMode);
  const originalFont = useRef(localStorage.getItem("selectedFont") || "none");
  const originalFontSize = useRef(localStorage.getItem("selectedFontSize") || "16px");

  const fontSizeScaleMap = {
    "12px": 0.75,
    "14px": 0.875,
    "16px": 1.0,
    "20px": 1.25,
    "24px": 1.5,
  };

  useEffect(() => {
    const fonts = {
      none: "",
      dyslexia: '"OpenDyslexic", sans-serif',
      serif: "Georgia, serif",
      sansSerif: "Arial, sans-serif",
      monospace: '"Courier New", monospace',
    };
    document.body.style.fontFamily = fonts[localFont] || "";
    const scale = fontSizeScaleMap[localFontSize] || 1.0;
    document.documentElement.style.setProperty("--root-font-size", `${16 * scale}px`);
  }, [localFont, localFontSize]);

  useEffect(() => {
    setColorblindMode(localMode);
  }, [localMode, setColorblindMode]);

  useEffect(() => {
    return () => {
    };
  }, [setColorblindMode, setAccessibilityHidden, setLocalFontSize]);

  const handleDarkMode = async (e) => {
    const newDarkModePreference = e.target.value === "on";
    setDarkMode(newDarkModePreference);
    if (newDarkModePreference) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    try {
      await setUserPreference({
        newDarkMode: newDarkModePreference,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating dark mode:", error);
      setDarkMode(!newDarkModePreference);
    }
  };

  const handleInActive = async (e) => {
    const newHideInActive = e.target.value === "on";
    setHideInActive(newHideInActive);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating hide inactive:", error);
      setHideInActive(!newHideInActive);
    }
  };

  const handleDenied = async (e) => {
    const newHideDenied = e.target.value === "on";
    setHideDenied(newHideDenied);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating hide denied:", error);
      setHideDenied(!newHideDenied);
    }
  };

  const handleToggleAccessibility = async (e) => {
    const newHiddenState = e.target.value === "on";
    setAccessibilityHidden(newHiddenState);
    localStorage.setItem("accessibilityHidden", newHiddenState.toString());
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: newHiddenState,
      });
    } catch (error) {
      console.error("Error updating accessibility hidden:", error);
      setAccessibilityHidden(!newHiddenState);
      localStorage.setItem("accessibilityHidden", (!newHiddenState).toString());
    }
  };

  const handleColorblindChange = async (e) => {
    const newLocalMode = e.target.value;
    setLocalMode(newLocalMode);
    setColorblindMode(newLocalMode);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: newLocalMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating colorblind mode:", error);
      setLocalMode(originalColorblind.current);
      setColorblindMode(originalColorblind.current);
    }
  };

  const handleFontChange = async (e) => {
    const newLocalFont = e.target.value;
    setLocalFont(newLocalFont);
    localStorage.setItem("selectedFont", newLocalFont);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: newLocalFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating font:", error);
      setLocalFont(originalFont.current);
      localStorage.setItem("selectedFont", originalFont.current);
    }
  };

  const handleFontSizeChange = async (e) => {
    const newLocalFontSize = e.target.value;
    setLocalFontSize(newLocalFontSize);
    localStorage.setItem("selectedFontSize", newLocalFontSize);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: newLocalFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating font size:", error);
      setLocalFontSize(originalFontSize.current);
      localStorage.setItem("selectedFontSize", originalFontSize.current);
    }
  };

  return (
    <div id="preferences" className="w-full px-4 py-6 sm:w-3/4 sm:px-12">
      <div className="max-w-4xl rounded-lg bg-white p-6 shadow-lg dark:bg-stone-900">
        <h2 className="text-xl font-bold mb-6 dark:text-gray-200">Preferences</h2>
        <div className="space-y-6">
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Dark Mode
            </label>
            <select
              value={darkMode ? "on" : "off"}
              onChange={handleDarkMode}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Hide Inactive
            </label>
            <select
              value={hideInActive ? "on" : "off"}
              onChange={handleInActive}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Hide Denied
            </label>
            <select
              value={hideDenied ? "on" : "off"}
              onChange={handleDenied}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Colorblind Mode
            </label>
            <select
              value={localMode}
              onChange={handleColorblindChange}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="none">Default</option>
              <option value="protanopia">Protanopia</option>
              <option value="protanomaly">Protanomaly</option>
              <option value="deuteranopia">Deuteranopia</option>
              <option value="tritanopia">Tritanopia</option>
              <option value="tritanomaly">Tritanomaly</option>
              <option value="achromatopsia">Achromatopsia</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Font
            </label>
            <select
              value={localFont}
              onChange={handleFontChange}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="none">Default</option>
              <option value="dyslexia">Dyslexic</option>
              <option value="serif">Serif</option>
              <option value="sansSerif">Sans-serif</option>
              <option value="monospace">Monospace</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Font Size
            </label>
            <select
              value={localFontSize}
              onChange={handleFontSizeChange}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="12px">Extra Small (12px)</option>
              <option value="14px">Small (14px)</option>
              <option value="16px">Medium (16px)</option>
              <option value="20px">Large (20px)</option>
              <option value="24px">Extra Large (24px)</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Hide Accessibility
            </label>
            <select
              value={accessibilityHidden ? "on" : "off"}
              onChange={handleToggleAccessibility}
              className="w-full sm:w-1/2 rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}